<template>
	<div class="chat-voice-message" :class="[mode, { playing: audioPlayState === 'PLAYING', mine }]"
		@click.stop="onPlayVoice()">
		<div class="voice-body">
			<span class="iconfont icon-voice-play voice-icon"></span>
			<div class="voice-wave">
				<span class="wave-bar" v-for="i in 5" :key="i"></span>
			</div>
			<span class="voice-duration">{{ displayDuration }}"</span>
			<span v-if="audioPlayState === 'PLAYING'" class="iconfont icon-pause play-indicator"></span>
			<span v-else-if="audioPlayState === 'PAUSE'" class="iconfont icon-play play-indicator"></span>
		</div>
	</div>
</template>

<script>
export default {
	name: 'ChatVoiceMessage',
	props: {
		url: {
			type: String,
			default: ''
		},
		duration: {
			type: Number,
			default: 0
		},
		mine: {
			type: Boolean,
			default: false
		},
		mode: {
			type: String,
			default: 'chat',
			validator: (value) => ['chat', 'quote', 'history'].includes(value)
		}
	},
	data() {
		return {
			audioPlayState: 'STOP',
			audioEl: null
		};
	},
	computed: {
		displayDuration() {
			return this.duration || 0;
		}
	},
	methods: {
		onPlayVoice() {
			if (!this.url) {
				return;
			}
			if (this.audioPlayState === 'STOP') {
				if (this.audioEl) {
					this.audioEl.pause();
				}
				this.audioEl = new Audio(this.url);
				this.audioEl.onended = () => {
					this.audioPlayState = 'STOP';
					this.emitAudioStateChange();
				};
				this.audioEl.onerror = () => {
					this.audioPlayState = 'STOP';
					this.emitAudioStateChange();
				};
				this.audioEl.play();
				this.audioPlayState = 'PLAYING';
			} else if (this.audioPlayState === 'PLAYING') {
				this.audioEl.pause();
				this.audioPlayState = 'PAUSE';
			} else if (this.audioPlayState === 'PAUSE') {
				this.audioEl.play();
				this.audioPlayState = 'PLAYING';
			}
			this.emitAudioStateChange();
		},
		emitAudioStateChange() {
			this.$emit('audioStateChange', this.audioPlayState);
		},
		stopPlayAudio() {
			if (this.audioEl) {
				this.audioEl.pause();
				this.audioEl = null;
			}
			this.audioPlayState = 'STOP';
		}
	},
	beforeDestroy() {
		this.stopPlayAudio();
	}
}
</script>

<style lang="scss" scoped>
.chat-voice-message {
	display: inline-flex;
	align-items: center;
	cursor: pointer;
	user-select: none;
	transition: opacity 0.2s ease;

	&:hover {
		opacity: 0.85;
	}

	.voice-body {
		display: flex;
		align-items: center;
		gap: 8px;
	}

	.voice-icon {
		font-size: 19px;
		flex-shrink: 0;
	}

	.voice-wave {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 3px;
		width: 36px;
		height: 20px;
		flex-shrink: 0;

		.wave-bar {
			width: 3px;
			height: 8px;
			border-radius: 2px;
			background: currentColor;
			opacity: 0.45;
			flex-shrink: 0;
		}
	}

	&.playing .voice-wave .wave-bar {
		animation: voice-wave-bounce 0.8s ease-in-out infinite;

		&:nth-child(1) {
			animation-delay: 0s;
		}

		&:nth-child(2) {
			animation-delay: 0.1s;
		}

		&:nth-child(3) {
			animation-delay: 0.2s;
		}

		&:nth-child(4) {
			animation-delay: 0.3s;
		}

		&:nth-child(5) {
			animation-delay: 0.4s;
		}
	}

	.voice-duration {
		font-size: var(--im-font-size-small);
		flex-shrink: 0;
		white-space: nowrap;
	}

	.play-indicator {
		font-size: 17px;
		flex-shrink: 0;
		opacity: 0.9;
	}

	&.chat {
		padding: 8px 12px;
		line-height: 26px;
		background: var(--im-background);
		border-radius: 10px;
		font-size: var(--im-font-size);
		text-align: left;

		&.mine {
			background: var(--im-color-primary-light-2);
			color: white;

			.voice-body {
				flex-direction: row-reverse;
			}

			.voice-icon {
				transform: scaleX(-1);
			}
		}
	}

	&.history {
		padding: 8px 12px;
		line-height: 26px;
		background: var(--im-background);
		border-radius: 10px;
		font-size: var(--im-font-size);
		text-align: left;
	}

	&.quote {
		padding: 4px 8px;
		background: white;
		border-radius: 6px;
		color: var(--im-text-color);

		.voice-body {
			gap: 6px;
		}

		.voice-wave {
			gap: 2px;
			width: 28px;
			height: 16px;

			.wave-bar {
				width: 2px;
				height: 6px;
			}
		}

		&.playing .voice-wave .wave-bar {
			animation-name: voice-wave-bounce-quote;
		}

		.voice-duration {
			font-size: inherit;
		}
	}
}

@keyframes voice-wave-bounce {

	0%,
	100% {
		height: 6px;
		opacity: 0.35;
	}

	50% {
		height: 16px;
		opacity: 1;
	}
}

@keyframes voice-wave-bounce-quote {

	0%,
	100% {
		height: 4px;
		opacity: 0.35;
	}

	50% {
		height: 12px;
		opacity: 1;
	}
}
</style>
